import { useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { unitMapping, type Unit } from '#src/lib/style.ts';

import { colorsByTime, type TimeKey } from '../colorsByTime.ts';

const AbsoluteSvg = styled.svg`
  position: absolute;
  bottom: 4%;
  right: 0%;
`;

const titleMessages = defineMessages({
  open: {
    defaultMessage: 'One life',
  },
  closed: {
    defaultMessage: 'One life lost',
  },
  locked: {
    defaultMessage: 'One life locked',
  },
  editable: {
    defaultMessage: 'One life editable',
  },
});

const strokeByTime: Record<TimeKey, string> = {
  '0': '#5CB800',
  '25': '#88B300',
  '50': '#FF8000',
  '75': '#FF4422',
  '90': '#AA0000',
  '100': '#2A2A2A',
  locked: '#888888',
};

type Props = {
  size?: Unit;
  status?: 'open' | 'closed' | 'locked' | 'editable';
  time?: TimeKey;
};

export const HeartIcon = ({
  size = 2,
  status = 'closed',
  time = '25',
}: Props) => {
  const fillId = useId();
  const filterId = useId();
  const titleId = useId();
  const { formatMessage } = useIntl();

  const colors = status !== 'closed' ? colorsByTime[time] : null;
  const isFilled = colors !== null;
  const hasGlow = status === 'open' && time !== '0' && time !== 'locked';
  const strokeColor = strokeByTime[time];
  const opacity = status === 'open' ? undefined : 0.5;

  return (
    <div className="relative inline-flex" style={{ opacity }}>
      <svg
        className="block"
        style={{ width: unitMapping[size], height: unitMapping[size] }}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-labelledby={titleId}
      >
        <title id={titleId}>{formatMessage(titleMessages[status])}</title>
        <g filter={hasGlow ? `url(#${filterId})` : undefined}>
          <path
            d="M39.25 6.29492V19.4238L19.9736 38.9316L0.75 19.4248V6.29492L10.0127 0.869141L19.6475 6.5127L20.0264 6.73535L20.4062 6.5127L30.0127 0.869141L39.25 6.29492Z"
            fill={isFilled ? `url(#${fillId})` : 'none'}
            stroke={strokeColor}
            strokeWidth="1.5"
          />
        </g>
        <defs>
          {!!isFilled && (
            <radialGradient
              id={fillId}
              cx="0"
              cy="0"
              r="1"
              gradientUnits="userSpaceOnUse"
              gradientTransform="translate(20 8) scale(30)"
            >
              <stop stopColor={colors.start} />
              <stop offset="1" stopColor={colors.end} />
            </radialGradient>
          )}
          <filter
            id={filterId}
            x="-0.000112653"
            y="9.41753e-06"
            width="69.3078"
            height="69.3078"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feMorphology
              radius="1.48019"
              operator="dilate"
              in="SourceAlpha"
              result="effect1_dropShadow_10673_120908"
            />
            <feOffset />
            <feGaussianBlur stdDeviation="6.58686" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_10673_120908"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_10673_120908"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="2.95127" />
            <feGaussianBlur stdDeviation="1.11015" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.75 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect2_innerShadow_10673_120908"
            />
          </filter>
        </defs>
      </svg>
      {status === 'locked' && (
        <AbsoluteSvg
          style={{
            width: `calc(${size} * var(--half-unit))`,
            height: `calc(${size} * var(--half-unit))`,
          }}
          viewBox="0 0 8 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2 3V2C2 0.89543 2.89543 0 4 0C5.10457 0 6 0.895431 6 2V3H7V8H1V3H2ZM3 2C3 1.44772 3.44772 1 4 1C4.55228 1 5 1.44772 5 2V3H3V2ZM3.5 6.5V4.5H4.5V6.5H3.5Z"
            fill="white"
          />
        </AbsoluteSvg>
      )}
      {status === 'editable' && (
        <AbsoluteSvg
          style={{
            width: `calc(${size} * var(--half-unit))`,
            height: `calc(${size} * var(--half-unit))`,
          }}
          viewBox="0 0 8 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6 0L8 2L2.5 7.5L0 8L0.5 5.5L6 0ZM6 1L5 2L6 3L7 2L6 1Z"
            fill="white"
          />
        </AbsoluteSvg>
      )}
    </div>
  );
};
